import App from './App.tsx';
import ChatPage from './pages/ChatPage.tsx';
import NotFound from './pages/NotFound.tsx';
import BotExplorePage from './pages/BotExplorePage.tsx';
import BotEditPage from './pages/BotEditPage.tsx';
import BotKbEditPage from './features/knowledgeBase/pages/BotKbEditPage.tsx';
import BotApiSettingsPage from './pages/BotApiSettingsPage.tsx';
import AdminSharedBotAnalyticsPage from './pages/AdminSharedBotAnalyticsPage.tsx';
import AdminApiManagementPage from './pages/AdminApiManagementPage.tsx';
import AdminBotManagementPage from './pages/AdminBotManagementPage.tsx';
import EzAuthPage from './pages/EzAuthPage.tsx';
import EzAuthAdminPage from './pages/EzAuthAdminPage.tsx';
import EzAuthGroupAdminPage from './pages/EzAuthGroupAdminPage.tsx';
import { useTranslation } from 'react-i18next';
import {
  createBrowserRouter,
  matchRoutes,
  RouteObject,
  useLocation,
} from 'react-router-dom';
import { useMemo } from 'react';
import SubmitFilesPage from './pages/SubmitFilesPage.tsx';  // 2024-11-08 k_nagayama submit追加
import AdminSubmitFilesPage from './pages/AdminSubmitFilesPage.tsx';

const KB_ENABLED: boolean = import.meta.env.VITE_APP_ENABLE_KB === 'true';

const rootChildren = [
  {
    path: '/',
    element: <ChatPage />,
  },
  {
    path: '/bot/explore',
    element: <BotExplorePage />,
  },
  {
    path: '/bot/new',
    element: KB_ENABLED ? <BotKbEditPage /> : <BotEditPage />,
  },
  {
    path: '/bot/edit/:botId',
    element: KB_ENABLED ? <BotKbEditPage /> : <BotEditPage />,
  },
  {
    path: '/bot/api-settings/:botId',
    element: <BotApiSettingsPage />,
  },
  {
    path: '/bot/:botId',
    element: <ChatPage />,
  },
  {
    path: '/admin/shared-bot-analytics',
    element: <AdminSharedBotAnalyticsPage />,
  },
  {
    path: '/admin/api-management',
    element: <AdminApiManagementPage />,
  },
  {
    path: '/admin/bot/:botId',
    element: <AdminBotManagementPage />,
  },
  {
    path: '/:conversationId',
    element: <ChatPage />,
  },
  {
    path: '/ez_auth/admin',
    element: <EzAuthAdminPage />,
  },
  {
    path: '/ez_auth/group-admin',
    element: <EzAuthGroupAdminPage />,
  },
  // 2024-11-08 k_nagayama submit追加
  {
    path: '/submit-files/submit',
    element: <SubmitFilesPage />,
  },
  {
    path: '/submit-files/admin',
    element: <AdminSubmitFilesPage />,
  },
  // 2024-11-08 k_nagayama submit追加 ここまで
  {
    path: '*',
    element: <NotFound />,
  },
] as const;


const routes = [
  {
    path: '/ez_auth/:userId/:ezAuthId',
    element: <EzAuthPage />,
  },
  {
    path: '/',
    element: <App />,
    children: rootChildren,
  },
];

type AllPaths = (typeof rootChildren)[number]['path'];

const getAllPaths = (routes: typeof rootChildren): AllPaths[] =>
  routes.map(({ path }) => path);

export const allPaths = getAllPaths(rootChildren);

export const usePageLabel = () => {
  const { t } = useTranslation();
  const pageLabel: { path: (typeof allPaths)[number]; label: string }[] = [
    { path: '/bot/explore', label: t('bot.explore.label.pageTitle') },
    {
      path: '/admin/shared-bot-analytics',
      label: t('admin.sharedBotAnalytics.label.pageTitle'),
    },
    {
      path: '/admin/api-management',
      label: t('admin.apiManagement.label.pageTitle'),
    },
  ];

  const getPageLabel = (pagePath: (typeof allPaths)[number]) =>
    pageLabel.find(({ path }) => path === pagePath)?.label;
  return { pageLabel, getPageLabel };
};

export const router = createBrowserRouter(routes as unknown as RouteObject[]);

type ConversationRoutes = { path: (typeof allPaths)[number] }[];

export const usePageTitlePathPattern = () => {
  const location = useLocation();

  const conversationRoutes: ConversationRoutes = useMemo(
    () => [
      { path: '/:conversationId' },
      { path: '/bot/:botId' },
      { path: '/' },
      { path: '*' },
    ],
    []
  );
  const notConversationRoutes = useMemo(
    () =>
      allPaths
        .filter(
          (pattern) => !conversationRoutes.find(({ path }) => path === pattern)
        )
        .map((pattern) => ({ path: pattern })),
    [conversationRoutes]
  );
  const matchedRoutes = useMemo(() => {
    return matchRoutes(notConversationRoutes, location);
  }, [location, notConversationRoutes]);

  const pathPattern = useMemo(
    () => matchedRoutes?.[0]?.route.path ?? '/',
    [matchedRoutes]
  );

  const isConversationOrNewChat = useMemo(
    () => !matchedRoutes?.length,
    [matchedRoutes]
  );
  return { isConversationOrNewChat, pathPattern };
};
